import type { Rng } from '@campfire/engine';

/** A die face, 1–6. */
export type Face = 1 | 2 | 3 | 4 | 5 | 6;

/** Roll `n` six-sided dice from the injected rng. */
export function rollDice(n: number, rng: Rng): number[] {
  return Array.from({ length: n }, () => rng.int(6) + 1);
}

/** True when `held` is a set of distinct, in-range indices into `dice` (it may be empty or hold everything). */
export function validHold(dice: readonly number[], held: readonly number[]): boolean {
  if (!Array.isArray(held)) return false;
  const seen = new Set<number>();
  for (const i of held) {
    if (!Number.isInteger(i) || i < 0 || i >= dice.length || seen.has(i)) return false;
    seen.add(i);
  }
  return true;
}

/** Re-roll every die whose index is NOT in `held`; held dice keep their value and position. */
export function reroll(dice: readonly number[], held: readonly number[], rng: Rng): number[] {
  const keep = new Set(held);
  return dice.map((d, i) => (keep.has(i) ? d : rng.int(6) + 1));
}

/** How many dice show each face; index 0 is unused so `counts(d)[f]` reads naturally. */
export function faceCounts(dice: readonly number[]): number[] {
  const c = [0, 0, 0, 0, 0, 0, 0];
  for (const d of dice) c[d] = (c[d] ?? 0) + 1;
  return c;
}

export const sumDice = (dice: readonly number[]): number => dice.reduce((a, b) => a + b, 0);

/** Length of the longest run of consecutive faces present (1,2,3,4,6 -> 4). */
export function longestRun(dice: readonly number[]): number {
  const c = faceCounts(dice);
  let best = 0;
  let run = 0;
  for (let f = 1; f <= 6; f++) {
    run = (c[f] ?? 0) > 0 ? run + 1 : 0;
    best = Math.max(best, run);
  }
  return best;
}

/** Do the dice contain a straight of at least `len` consecutive faces? */
export const hasStraight = (dice: readonly number[], len: number): boolean => longestRun(dice) >= len;

/** Is the biggest group of equal dice at least `n` strong? (Three/four/five of a kind.) */
export const hasOfAKind = (dice: readonly number[], n: number): boolean => faceCounts(dice).some((c) => c >= n);

/** Indices of every dice showing `face`. */
export const indicesOf = (dice: readonly number[], face: number): number[] => dice.flatMap((d, i) => (d === face ? [i] : []));
